/**
 * FarmerCropPoster — one-tap "Post today's harvest" form for farmers.
 *
 * Rendered on FarmstandPage.jsx for the farmer's own farm. Pipes straight
 * into `/api/farmers-pulse/crop-post` so a farmer can drop a crop-post in
 * seconds — which auto-drafts a matching FoodPulse deal AND fires wishlist
 * alerts to any nearby shoppers watching that crop.
 *
 * Keeps the form intentionally lean: crop name (required), qty + unit,
 * price/unit, notes. Everything else (city, state, farm slug) is pulled
 * from the parent farmstand payload so the farmer doesn't retype it.
 */
import { useState } from "react";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { Tractor, Loader2, Send, PackageCheck, X } from "lucide-react";

const UNITS = ["lb", "kg", "each", "dozen", "bunch", "pint", "quart", "bushel", "case"];

export default function FarmerCropPoster({ farmSlug, city, state, country, autoPublish = true, onPosted }) {
  const [expanded, setExpanded] = useState(false);
  const [cropName, setCropName] = useState("");
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("lb");
  const [price, setPrice] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastPosted, setLastPosted] = useState(null);

  const canSubmit = cropName.trim().length >= 2 && !busy;

  const submit = async () => {
    if (!canSubmit || !farmSlug) return;
    setBusy(true);
    try {
      const payload = {
        farm_slug: farmSlug,
        crop_name: cropName.trim(),
        qty: qty ? Number(qty) : null,
        unit: unit || null,
        price: price ? Number(price) : null,
        city: city || null,
        state: state || null,
        country: country || "United States",
        notes: notes.trim() || null,
        auto_publish_deal: autoPublish,
      };
      const r = await http.post("/farmers-pulse/crop-post", payload);
      const alerted = r.data?.wishlist_alerts?.alerted || 0;
      toast.success(
        alerted > 0
          ? `Live! ${alerted} shopper${alerted === 1 ? "" : "s"} pinged.`
          : (r.data?.message || "Crop posted"),
      );
      setLastPosted({ crop: cropName.trim(), alerted, dealId: r.data?.deal_id });
      // Reset the form so the farmer can post another crop immediately
      setCropName("");
      setQty("");
      setPrice("");
      setNotes("");
      if (typeof onPosted === "function") onPosted(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Post failed — try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        data-testid="farmer-crop-poster-open"
        className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
      >
        <Tractor className="h-4 w-4" /> Post today's harvest
      </button>
    );
  }

  return (
    <section
      className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-5"
      data-testid="farmer-crop-poster"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-content-center rounded-full bg-white">
              <Tractor className="h-4 w-4 text-emerald-700" />
            </span>
            <div className="text-sm font-semibold text-emerald-900">Post today's harvest</div>
          </div>
          <p className="mt-1 text-xs text-emerald-800/80">
            Publishes to FoodPulse instantly and pings shoppers who wishlisted this crop in {city || "your area"}.
          </p>
        </div>
        <button
          onClick={() => setExpanded(false)}
          data-testid="farmer-crop-poster-close"
          className="rounded-full p-1 text-emerald-700 hover:bg-emerald-100"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-6">
        <div className="sm:col-span-3">
          <label className="mb-0.5 block text-[11px] font-medium text-emerald-900">Crop *</label>
          <input
            value={cropName}
            onChange={(e) => setCropName(e.target.value)}
            placeholder="e.g. Heirloom Tomato"
            data-testid="farmer-crop-poster-crop"
            className="w-full rounded-md border border-emerald-200 bg-white px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="mb-0.5 block text-[11px] font-medium text-emerald-900">Qty</label>
          <input
            type="number"
            min="0"
            step="0.5"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            data-testid="farmer-crop-poster-qty"
            className="w-full rounded-md border border-emerald-200 bg-white px-2 py-1.5 text-sm"
          />
        </div>
        <div>
          <label className="mb-0.5 block text-[11px] font-medium text-emerald-900">Unit</label>
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            data-testid="farmer-crop-poster-unit"
            className="w-full rounded-md border border-emerald-200 bg-white px-2 py-1.5 text-sm"
          >
            {UNITS.map((u) => (<option key={u} value={u}>{u}</option>))}
          </select>
        </div>
        <div>
          <label className="mb-0.5 block text-[11px] font-medium text-emerald-900">Price / {unit}</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            data-testid="farmer-crop-poster-price"
            className="w-full rounded-md border border-emerald-200 bg-white px-2 py-1.5 text-sm"
          />
        </div>
        <div className="sm:col-span-6">
          <label className="mb-0.5 block text-[11px] font-medium text-emerald-900">Notes (optional)</label>
          <input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g. Just picked, 4 varieties, meet at the barn 3–6pm."
            data-testid="farmer-crop-poster-notes"
            className="w-full rounded-md border border-emerald-200 bg-white px-2 py-1.5 text-sm"
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11px] text-emerald-800/80">
          Posting from <b>{farmSlug}</b>{city ? ` · ${city}` : ""}{state ? `, ${state}` : ""}
        </div>
        <button
          onClick={submit}
          disabled={!canSubmit}
          data-testid="farmer-crop-poster-submit"
          className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:bg-emerald-300"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {busy ? "Publishing…" : "Publish to FoodPulse"}
        </button>
      </div>

      {lastPosted && (
        <div
          className="mt-3 flex items-center gap-2 rounded-lg bg-white/80 px-3 py-2 text-xs text-emerald-800"
          data-testid="farmer-crop-poster-last"
        >
          <PackageCheck className="h-3.5 w-3.5" />
          <span>
            Last posted: <b>{lastPosted.crop}</b>
            {typeof lastPosted.alerted === "number" && (
              <span> · pinged {lastPosted.alerted} matching shopper{lastPosted.alerted === 1 ? "" : "s"}</span>
            )}
          </span>
        </div>
      )}
    </section>
  );
}
